/*
 * Bootstrap 4 modal with react-bootstrap's Modal API, written by hand because react-bootstrap animates through
 * react-transition-group, which calls ReactDOM.findDOMNode (removed in React 19). It renders the same markup and
 * classes so the existing CSS keeps applying, and supports only the props AMI uses.
 * New pop-ups should use components/library/Modal instead.
 */
import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Swal from "sweetalert2";

const ModalContext = createContext(null);

/**
 * Root elements of every open BootstrapModal, in the order they opened; shared by all instances, so it lives at
 * module level (state belongs to one instance). Only OpenModal's mount effect adds to it and its cleanup removes.
 * - The last entry is the top modal: only it closes on Escape.
 * - The first to open adds `modal-open` to <body> (scroll lock); the last to close removes it.
 * @type {HTMLElement[]}
 */
const openModalStack = [];

/**
 * Length of Bootstrap 4's `.modal.fade` transition; a closing modal stays mounted this long so it can fade out.
 */
const TRANSITION_MS = 300;

/**
 * Plays Bootstrap 4's modal slide-and-fade: the modal mounts with `show` true and gets the `show` class one forced
 * reflow later, so the `.fade` to `.fade.show` CSS transition runs. When `show` turns false the class comes off first
 * and the modal stays mounted for `TRANSITION_MS`, so it fades out before unmounting.
 * @param {boolean} show Whether the modal should be open.
 * @returns {{isMounted: boolean, hasShowClass: boolean}}
 */
function useFadeTransition(show) {
    const [isMounted, setIsMounted] = useState(show);
    const [hasShowClass, setHasShowClass] = useState(false);

    useEffect(() => {
        if (show) {
            setIsMounted(true);
            return undefined;
        }

        setHasShowClass(false);
        const exitTimer = setTimeout(() => setIsMounted(false), TRANSITION_MS);
        return () => clearTimeout(exitTimer);
    }, [show]);

    useEffect(() => {
        if (show && isMounted) {
            document.body.getBoundingClientRect();
            setHasShowClass(true);
        }
    }, [show, isMounted]);

    return {
        isMounted: show || isMounted,
        hasShowClass,
    };
}

/**
 * Bootstrap 4 modal with react-bootstrap's API: same markup, rendered into document.body, unmounted once it has
 * faded out.
 * A backdrop click calls `onHide`, and so does Escape unless a Swal is open on top or a control inside (react-select)
 * already handled it.
 * @param {Object} props `show`, `onHide`, `size` ("sm"|"md"|"lg"|"xl"), `centered`, `className` (on `.modal`),
 * `backdropClassName`; other props go on `.modal-dialog`, as react-bootstrap does.
 * @example
 * <Modal show={open} onHide={close} size="md" centered>
 *     <Modal.Header closeButton><Modal.Title>Title</Modal.Title></Modal.Header>
 *     <Modal.Body>...</Modal.Body>
 * </Modal>
 */
export function Modal({ show, ...props }) {
    const { isMounted, hasShowClass } = useFadeTransition(show);

    if (!isMounted) {
        return null;
    }

    return createPortal(<OpenModal {...props} hasShowClass={hasShowClass} />, document.body);
}

/**
 * The mounted part of Modal, split out so its effects only run while the modal is on screen.
 * It focuses the dialog unless a field inside took focus, and on close refocuses the opener, read on the first
 * render before an autoFocus field can move it. Like react-bootstrap, a click that starts in the dialog and ends
 * on the backdrop doesn't close it.
 * @param {Object} props Modal's props without `show`, plus `hasShowClass` from useFadeTransition.
 */
function OpenModal({
    onHide,
    size,
    centered,
    className,
    backdropClassName,
    children,
    hasShowClass,
    "aria-labelledby": ariaLabelledby,
    ...dialogProps
}) {
    const modalRef = useRef(null);
    const openerRef = useRef(document.activeElement);
    const onHideRef = useRef(onHide);
    const mouseDownInDialogRef = useRef(false);
    const ignoreBackdropClickRef = useRef(false);
    onHideRef.current = onHide;

    useEffect(() => {
        const modalElement = modalRef.current;
        const openerElement = openerRef.current;
        const bodyStyle = document.body.style;
        const previousPaddingRight = bodyStyle.paddingRight;

        if (openModalStack.length === 0) {
            const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
            if (scrollbarWidth > 0) {
                bodyStyle.paddingRight = `${scrollbarWidth}px`;
            }
            document.body.classList.add("modal-open");
        }
        openModalStack.push(modalElement);
        if (!modalElement.contains(document.activeElement)) {
            modalElement.focus();
        }

        const handleKeyDown = (event) => {
            const isTopModal = openModalStack[openModalStack.length - 1] === modalElement;
            if (event.key === "Escape" && isTopModal && !event.defaultPrevented && !Swal.isVisible() && onHideRef.current) {
                onHideRef.current();
            }
        };
        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("keydown", handleKeyDown);
            openModalStack.splice(openModalStack.indexOf(modalElement), 1);
            if (openModalStack.length === 0) {
                document.body.classList.remove("modal-open");
                bodyStyle.paddingRight = previousPaddingRight;
            }
            if (openerElement && document.body.contains(openerElement)) {
                openerElement.focus();
            }
        };
    }, []);

    const handleMouseUp = (event) => {
        if (mouseDownInDialogRef.current && event.target === event.currentTarget) {
            ignoreBackdropClickRef.current = true;
        }
        mouseDownInDialogRef.current = false;
    };

    const handleClick = (event) => {
        if (ignoreBackdropClickRef.current || event.target !== event.currentTarget) {
            ignoreBackdropClickRef.current = false;
            return;
        }
        if (onHide) {
            onHide();
        }
    };

    const backdropClasses = ["modal-backdrop", "fade", hasShowClass && "show", backdropClassName]
        .filter(Boolean)
        .join(" ");
    const modalClasses = [className, "modal", "fade", hasShowClass && "show"].filter(Boolean).join(" ");
    const dialogClasses = ["modal-dialog", size && `modal-${size}`, centered && "modal-dialog-centered"]
        .filter(Boolean)
        .join(" ");

    return (
        <ModalContext.Provider value={onHide}>
            <div className={backdropClasses} />
            <div
                ref={modalRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={ariaLabelledby}
                tabIndex={-1}
                className={modalClasses}
                style={{ display: "block" }}
                onClick={handleClick}
                onMouseUp={handleMouseUp}
            >
                <div
                    {...dialogProps}
                    className={dialogClasses}
                    onMouseDown={() => {
                        mouseDownInDialogRef.current = true;
                    }}
                >
                    <div className="modal-content">{children}</div>
                </div>
            </div>
        </ModalContext.Provider>
    );
}

/**
 * `.modal-header`; with `closeButton` it adds Bootstrap's × button, which calls the Modal's `onHide`.
 * @param {boolean} [props.closeButton] Show the × button.
 * @param {string} [props.className] Extra classes next to `modal-header`.
 * @param {React.ReactNode} props.children Header content, usually a Modal.Title.
 */
function ModalHeader({ closeButton, className, children }) {
    const onHide = useContext(ModalContext);

    return (
        <div className={["modal-header", className].filter(Boolean).join(" ")}>
            {children}
            {closeButton && (
                <button type="button" className="close" onClick={onHide}>
                    <span aria-hidden="true">×</span>
                    <span className="sr-only">Close</span>
                </button>
            )}
        </div>
    );
}

/**
 * `.modal-title`, styled as an h4 like react-bootstrap's.
 * @param {React.ReactNode} props.children Title content.
 */
function ModalTitle({ children }) {
    return (
        <div className="modal-title h4">{children}</div>
    );
}

/**
 * `.modal-body`; also exported as `ModalBody`, matching react-bootstrap's named export.
 * @param {string} [props.className] Extra classes next to `modal-body`.
 * @param {React.ReactNode} props.children Body content.
 */
export function ModalBody({ className, children }) {
    return (
        <div className={["modal-body", className].filter(Boolean).join(" ")}>{children}</div>
    );
}

Modal.Header = ModalHeader;
Modal.Title = ModalTitle;
Modal.Body = ModalBody;
